import React, { Fragment, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useTenants } from '../../hooks/tenants/useTenants';
import { QueueStatusFilter, UseParkingQueueReturn, useParkingQueueMutations, useParkingQueueView } from '../../hooks/parkingQueue';
import { describeWaited, groupLabel, spotMatchesEntry, SpotRef } from '../../Helpers/parkingQueueUtils';
import { describeQueueOrder } from '../../types/parkingQueueConfig';
import { ParkingQueueEntry, ParkingQueueEntryInput } from '../../types/parkingQueue';
import { TenantKind } from '../../types/Enums';
import ParkingQueueEntryModal from './ParkingQueueEntryModal';
import ParkingQueueEntryDetailModal from './ParkingQueueEntryDetailModal';
import { internalText, wantedAreasText, wishesText } from './queueLabels';
import '../Parking/Parking.css';
import './ParkingQueue.css';

const STATUS_OPTIONS: { value: QueueStatusFilter; label: string }[] = [
    { value: 'active', label: 'I kö' }, { value: 'closed', label: 'Avslutade' }, { value: 'all', label: 'Alla' },
];

/**
 * The Kö tab of /parking-queue. URL params: `?entry={key}` opens the detail, `?new=1` the create form,
 * `?spot={b}/{a}/{s}` filters to entries the spot matches and preselects the spot in an entry's
 * Nytt hyresavtal.
 */
const ParkingQueueBoard: React.FC<{ queue: UseParkingQueueReturn }> = ({ queue }) => {
    const isAdmin = useIsAdmin();
    const mutations = useParkingQueueMutations(queue.refresh);
    const { tenantsByKey, getTenantName } = useTenants();
    const { statusFilter, setStatusFilter } = useParkingQueueView();
    const [params, setParams] = useSearchParams();
    const [editing, setEditing] = useState<ParkingQueueEntry | null>(null);

    const setParam = (key: string, value: string | null) => {
        const next = new URLSearchParams(params);
        if (value === null) next.delete(key); else next.set(key, value);
        setParams(next, { replace: true });
    };
    const entryKey = params.get('entry');
    const isNew = params.get('new') === '1';
    const spotRef = useMemo<SpotRef | null>(() => {
        const [b, a, s] = (params.get('spot') ?? '').split('/');
        const spot = b && a && s ? queue.areasByBuilding[b]?.[a]?.spots?.[s] : undefined;
        return spot ? { buildingKey: b, areaKey: a, spotKey: s, spot } : null;
    }, [params, queue.areasByBuilding]);

    const spotNumber = spotRef ? queue.areasByBuilding[spotRef.buildingKey]?.[spotRef.areaKey]?.spots?.[spotRef.spotKey]?.number ?? '' : '';
    const statusRows = useMemo(() => queue.rows.filter(row =>
        statusFilter === 'all' || row.entry.status === statusFilter
    ), [queue.rows, statusFilter]);
    const rows = useMemo(() => spotRef ? statusRows.filter(row => spotMatchesEntry(row.entry, spotRef)) : statusRows, [statusRows, spotRef]);
    // The spot filter hides most of the queue, so the board says how much and offers the way back.
    const clearSpotFilter = () => {
        const next = new URLSearchParams(params);
        next.delete('spot');
        setParams(next, { replace: true });
    };
    const statusNoun = statusFilter === 'active' ? 'i kö' : statusFilter === 'closed' ? 'avslutade' : 'köplatser';
    const spotFilterText = (spotText: string) => rows.length === 0
        ? `Ingen av de ${statusRows.length} ${statusNoun} passar ${spotText}.`
        : `Visar ${rows.length} av ${statusRows.length} ${statusNoun} som passar ${spotText}.`;
    const selectedRow = entryKey ? queue.rows.find(r => r.entry.key === entryKey) ?? null : null;

    const saveEntry = async (input: ParkingQueueEntryInput) => {
        if (editing) await mutations.updateEntry(editing.key, input); else await mutations.createEntry(input);
        setEditing(null);
        setParam('new', null);
    };

    const kindText = (tenantKey: string) => tenantsByKey[tenantKey]?.kind === TenantKind.Company ? 'Företag' : 'Person';

    let lastGroup: string | null = null;
    return (
        <div className="parking-queue-page">
            {queue.error && <Alert variant="danger">{queue.error}</Alert>}
            {mutations.error && !editing && !isNew && !selectedRow && <Alert variant="danger" dismissible onClose={mutations.clearError}>{mutations.error}</Alert>}
            <div className="parking-tab-content std-box">
                <div className="parking-tab-content-header">
                    <div className="parking-queue-viewbar">
                        <Form.Select className="parking-queue-status" value={statusFilter} onChange={e => setStatusFilter(e.target.value as QueueStatusFilter)}>
                            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Form.Select>
                        <span className="text-muted small">Standardordning: {describeQueueOrder(queue.config)} <Link to="/settings/parking-queue">Ändra</Link></span>
                    </div>
                    {isAdmin && (
                        <div className="parking-queue-actions">
                            <Button variant="primary" onClick={() => setParam('new', '1')}><Plus size={16} className="me-1" />Ny köplats</Button>
                        </div>
                    )}
                </div>
                <div className="parking-tab-content-body">
                    {spotRef && (
                        <div className="parking-queue-spot-filter">
                            <span>{spotFilterText(`Plats ${spotNumber} · ${queue.areaLabel(spotRef.buildingKey, spotRef.areaKey)}`)}</span>
                            <Button variant="outline-secondary" size="sm" onClick={clearSpotFilter}>Visa hela kön</Button>
                        </div>
                    )}
                    {rows.length === 0 ? (
                        !spotRef && <p className="parking-empty">{statusFilter === 'closed' ? 'Inga avslutade köplatser.' : 'Ingen står i kö.'}</p>
                    ) : (
                        <table className="table parking-queue-table">
                            <thead><tr>
                                <th>Sökande</th><th>Intern/extern</th><th>Platser idag</th><th>Ködatum</th><th>Önskemål</th><th>Senast rörd</th><th>Senaste anteckning</th>
                            </tr></thead>
                            <tbody>
                                {rows.map(row => {
                                    const group = groupLabel(row, queue.config);
                                    const header = group !== null && group !== lastGroup ? group : null;
                                    lastGroup = group;
                                    const { entry } = row;
                                    const wishes = wishesText(row);
                                    return (
                                        <Fragment key={entry.key}>
                                            {header && <tr className="parking-queue-group"><td colSpan={7}>{header}</td></tr>}
                                            <tr className="parking-queue-row" onClick={() => setParam('entry', entry.key)}>
                                                <td><span className="pq-label">Sökande</span>{getTenantName(entry.tenantKey)} <span className="text-muted small">{kindText(entry.tenantKey)}</span></td>
                                                <td><span className="pq-label">Intern/extern</span>{internalText(row)}</td>
                                                <td title={row.heldSpots.map(s => s.label).join(', ')}><span className="pq-label">Platser idag</span>{row.heldSpots.length}</td>
                                                <td><span className="pq-label">Ködatum</span>{entry.queueDate} <span className="text-muted small">{describeWaited(row.daysWaited)}</span></td>
                                                <td><span className="pq-label">Önskemål</span>{wantedAreasText(entry.wantedAreas, queue)}{wishes.length > 0 && <div className="text-muted small">{wishes.join(' · ')}</div>}</td>
                                                <td><span className="pq-label">Senast rörd</span>{row.lastTouched.slice(0, 10)}</td>
                                                <td className="parking-queue-note"><span className="pq-label">Senaste anteckning</span>{row.latestNote ?? ''}</td>
                                            </tr>
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {(isNew || editing) && (
                <ParkingQueueEntryModal show onHide={() => { setEditing(null); setParam('new', null); mutations.clearError(); }} onSubmit={saveEntry}
                    entry={editing ?? undefined} queue={queue} submitting={mutations.submitting} error={mutations.error} />
            )}
            {selectedRow && !editing && (
                <ParkingQueueEntryDetailModal show onHide={() => { setParam('entry', null); mutations.clearError(); }} row={selectedRow} queue={queue} mutations={mutations}
                    onEdit={entry => setEditing(entry)}
                    spot={spotRef ? { buildingKey: spotRef.buildingKey, areaKey: spotRef.areaKey, spotKey: spotRef.spotKey } : undefined} />
            )}
        </div>
    );
};

export default ParkingQueueBoard;
